import { motion } from 'framer-motion';
import { isAppleTouchDevice } from '@helpers/appleTouchDevice.helper';
import React from 'react';
import styles from './index.module.scss';

interface MotionButtonProps {
  onClick?: () => void | undefined;
  children: React.ReactNode;
  className?: string;
  whileHover?: { scale?: number; opacity?: number };
  whileTap?: { scale?: number; opacity?: number };
  disabled?: boolean;
  transition?: { type?: string; stiffness?: number; damping?: number };
  type?: 'button' | 'submit' | 'reset';
  role?: string;
  id?: any;
  onKeyDown?: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  tabIndex?: number;
}

export const MotionButton: React.FC<MotionButtonProps> = ({
  onClick,
  children,
  className,
  whileHover,
  whileTap,
  disabled,
  type,
  role,
  transition: _transition,
  ...rest
}) => {
  // Experiment: plain <button> on iOS so Framer Motion does not register whileTap/touch handlers.
  // Hypothesis: active touch listeners contribute to late / unresponsive taps on iOS 26 WebKit.
  if (isAppleTouchDevice()) {
    return (
      <button
        onClick={onClick}
        className={className}
        disabled={disabled}
        type={type}
        role={role}
        id={rest.id}
        onKeyDown={rest.onKeyDown}
        tabIndex={rest.tabIndex}
      >
        {children}
      </button>
    );
  }

  return (
    <motion.button
      whileHover={whileHover}
      whileTap={whileTap}
      onClick={onClick}
      className={className}
      disabled={disabled}
      type={type}
      role={role}
      id={rest.id}
      onKeyDown={rest.onKeyDown}
      tabIndex={rest.tabIndex}
      {...rest}
    >
      {children}
    </motion.button>
  );
};

MotionButton.defaultProps = {
  onClick: undefined,
  className: styles.button,
  whileHover: { scale: 1.05 },
  whileTap: { scale: 0.95 },
  disabled: false,
  transition: { type: 'spring', stiffness: 300, damping: 20 },
  type: 'button',
  role: 'button',
  id: undefined,
  onKeyDown: undefined,
  tabIndex: 0,
};
